/**
 * Bill of Lading References Fields Component
 *
 * Renders the references section of the B/L form.
 * Follows OBReferencesFields exactly — each reference has a type (text)
 * and value (text). Users can add/remove references in edit mode.
 *
 * Common reference types: SI (Shipping Instruction), PO (Purchase Order),
 * BKG (Booking), HBL (House B/L), MBL (Master B/L), etc.
 */

import { useFieldArray } from 'react-hook-form';
import Input from '../common/Input';
import Button from '../common/Button';
import { getFieldIssues } from '../../utils/confidenceUtils';

/**
 * BLReferencesFields Component
 *
 * @param {Object} props
 * @param {Object} props.control - react-hook-form control
 * @param {Function} props.register - react-hook-form register
 * @param {Function} props.watch - react-hook-form watch (not used here, accepted for consistency)
 * @param {boolean} props.isEditMode - Whether fields are editable
 * @param {Array} props.issues - Unified issues array
 */
function BLReferencesFields({ control, register, watch, isEditMode, issues = [] }) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'references'
  });

  /**
   * Add a new empty reference
   */
  const addReference = () => {
    append({ type: '', value: '' });
  };

  return (
    <div className="space-y-4">
      {/* Section Title */}
      <div className="flex items-center justify-between border-b pb-2">
        <h3 className="text-lg font-medium text-gray-900">References</h3>
        {isEditMode && (
          <Button variant="secondary" size="sm" onClick={addReference}>
            + Add Reference
          </Button>
        )}
      </div>

      {/* Empty state */}
      {fields.length === 0 && (
        <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-md">
          No references added yet. Click "Add Reference" to add reference numbers.
        </div>
      )}

      {/* Reference entries */}
      {fields.map((field, index) => (
        <div key={field.id} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 flex-1">
              {/* Reference Type */}
              <Input
                label="Reference Type"
                {...register(`references.${index}.type`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `references[${index}].type`)}
                placeholder="e.g., SI, PO, BKG, HBL"
              />

              {/* Reference Value */}
              <Input
                label="Reference Value"
                {...register(`references.${index}.value`)}
                disabled={!isEditMode}
                issues={getFieldIssues(issues, `references[${index}].value`)}
                placeholder="Reference number or value"
              />
            </div>

            {/* Remove button */}
            {isEditMode && (
              <button
                type="button"
                onClick={() => remove(index)}
                className="text-red-500 hover:text-red-700 text-sm mt-8"
              >
                Remove
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default BLReferencesFields;
